/*
  Perfect Pass: a product list that loads its next page in place (Bar 10-20).

  Two uses:
  - Collection pages (sections/main-collection-product-grid.liquid): the next page loads by itself while the
    shopper scrolls, like a feed, twice; then a "Load more" button takes over so the footer stays reachable.
    Dawn's pagination stays in the page for crawlers and for browsers without JavaScript (hidden by CSS once
    this runs, see .pp-more--on in ring-light.css).
  - Featured collection sections on the homepage (sections/featured-collection.liquid, setting "View all loads
    the whole collection here"): one tap on "View all" adds every remaining product under the grid, page after
    page (data-all="1"; Bar + Raz, launch day 10-21), instead of opening the collection. No automatic loading
    there (data-auto="0"), so the homepage stays short until the shopper asks.

  Each load fetches that section only (Shopify's section rendering: ?section_id=…&page=N) with the page's current
  query (filters, sort, currency) and appends the page's cards, so the first screen costs the same as before and
  nothing above the thumb moves. Dawn's filters replace the whole grid container, which re-creates this element
  with a fresh state.

  Coming back from a product: the number of loaded pages and the scroll position are kept per URL in
  sessionStorage; on a back/forward or reload that isn't served from the browser's page cache, the same pages are
  fetched again and the list is scrolled to where it was.

  Attributes: data-section (section id), data-page (current), data-pages (total pages), data-per (products per
  page), data-total (products in the list), data-grid (selector of the <ul> holding the cards, default
  #product-grid), data-auto (automatic loads before the button takes over, default 2), data-all ("1": the button
  loads every remaining page in one go).
*/
class PPMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.onIntersect = this.onIntersect.bind(this);
    this.save = this.save.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  connectedCallback() {
    const root = this.closest('.shopify-section') || document;
    this.grid = root.querySelector(this.dataset.grid || '#product-grid');
    this.list = this.closest('.collection');
    this.button = this.querySelector('.pp-more__button');
    this.status = this.querySelector('.pp-more__status');
    this.section = this.dataset.section;
    this.page = Number(this.dataset.page) || 1;
    this.pages = Number(this.dataset.pages) || 1;
    this.perPage = Number(this.dataset.per) || 0;
    this.total = Number(this.dataset.total) || 0;
    this.autoLimit = this.dataset.auto === undefined ? 2 : Number(this.dataset.auto) || 0;
    if (!this.grid || !this.button || !this.section || this.page >= this.pages) {
      this.hidden = true;
      return;
    }
    this.label = this.button.textContent.trim();
    this.autoLoads = 0;
    this.loading = false;
    if (this.list) this.list.classList.add('pp-more--on');
    this.hidden = false;
    this.all = this.dataset.all === '1';
    this.folded = null;
    this.button.addEventListener('click', () => (this.all ? this.loadAll() : this.load('button')));
    if (this.autoLimit > 0) {
      // the next page starts loading about one and a half screens before the end of the list
      this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '900px 0px' });
      this.observer.observe(this);
    }
    this.key = 'pp-more:' + this.section + ':' + location.pathname + location.search;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('pagehide', this.save);
    this.updateStatus();
    this.restore();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('pagehide', this.save);
    clearTimeout(this.scrollTimer);
  }

  pageUrl(page) {
    const params = new URLSearchParams(location.search);
    params.set('page', page);
    params.set('section_id', this.section);
    return `${location.pathname}?${params}`;
  }

  // how: 'auto' (scrolling), 'button' (tap) or 'restore' (coming back)
  async load(how) {
    if (this.loading || this.page >= this.pages) return;
    this.loading = true;
    this.button.disabled = true;
    this.button.textContent = this.button.dataset.loading || 'Loading…';
    let items = [];
    let failed = false;
    try {
      const response = await fetch(this.pageUrl(this.page + 1));
      if (!response.ok) throw new Error(response.status);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const selector = this.dataset.grid || '#product-grid';
      items = Array.from(html.querySelectorAll(`${selector} > li`));
      items.forEach((item) => {
        // Dawn's reveal-on-scroll animation only watches the first page; new cards show straight away
        item.classList.add('scroll-trigger--cancel');
        item.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
        // featured collections number their slides 1…n on every page; keep ids unique
        if (item.id && document.getElementById(item.id)) item.id = `${item.id}-p${this.page + 1}`;
        this.grid.appendChild(item);
      });
      this.page += 1;
      if (how === 'auto') this.autoLoads += 1;
      if (how === 'button' && items[0]) {
        const link = items[0].querySelector('a[href]');
        if (link) link.focus({ preventScroll: true });
      }
      this.save();
    } catch (error) {
      // the button stays, so the shopper can try again. A failed automatic load counts as used, so a phone with
      // no signal near the end of the list never asks again every frame (launch review 10-21, T2-02): at most
      // the automatic loads allowed, then the button takes over.
      failed = true;
      if (how === 'auto') this.autoLoads += 1;
    }
    this.loading = false;
    this.button.disabled = false;
    this.button.textContent = this.label;
    if (this.loadingAll && this.page >= this.pages) this.loadingAll = false; // the last page: the status settles
    this.updateStatus();
    if (this.page >= this.pages) {
      this.finish();
    } else if (how === 'auto' && !failed) {
      // a short page can leave this element inside the margin with no new intersection event
      requestAnimationFrame(() => this.checkNear());
    }
    return items.length;
  }

  // "View all": every remaining page, one after the other, as each arrives. A page that fails leaves the button
  // for another tap, with everything loaded so far in place. With everything in, the same button reads
  // "Show less" (Bar 10-21): the list folds back to its first page, the section comes back into view, and the
  // next "View all" puts the folded cards straight back without fetching them again.
  async loadAll() {
    if (this.loading || this.loadingAll) return;
    if (this.folded) {
      this.folded.forEach((item) => this.grid.appendChild(item));
      this.folded = null;
      this.page = this.pages;
      this.save();
      this.updateStatus();
      this.finish();
      return;
    }
    if (this.page >= this.pages) {
      this.fold();
      return;
    }
    this.loadingAll = true;
    let first = true;
    while (this.page < this.pages) {
      const added = await this.load(first ? 'button' : 'all');
      if (!added) break;
      first = false;
    }
    this.loadingAll = false;
    this.updateStatus();
  }

  fold() {
    const items = Array.from(this.grid.querySelectorAll(':scope > li'));
    this.folded = items.slice(this.perPage || items.length);
    this.folded.forEach((item) => item.remove());
    this.page = 1;
    this.save();
    this.button.textContent = this.label;
    this.button.hidden = false;
    if (this.status) this.status.hidden = false;
    this.updateStatus();
    this.grid.scrollIntoView({ block: 'start' });
    this.button.focus({ preventScroll: true });
  }

  // the last page is in: the button goes, or on a "View all" list turns into "Show less"
  finish() {
    if (this.observer) this.observer.disconnect();
    if (this.all) {
      this.button.textContent = this.button.dataset.less || 'Show less';
      this.button.hidden = false;
      if (this.status) this.status.hidden = true;
    } else {
      this.button.hidden = true;
    }
  }

  checkNear() {
    if (this.autoLoads >= this.autoLimit || this.loading) return;
    const top = this.getBoundingClientRect().top;
    if (top < window.innerHeight + 900) this.load('auto');
  }

  onIntersect(entries) {
    if (this.autoLoads >= this.autoLimit) return;
    if (entries.some((entry) => entry.isIntersecting)) this.load('auto');
  }

  updateStatus() {
    if (!this.status || !this.total) return;
    const shown = Math.min(this.page * this.perPage, this.total);
    this.status.textContent = this.loadingAll ? `Showing ${shown} of ${this.total}…` : `${shown} of ${this.total}`;
  }

  onScroll() {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(this.save, 250);
  }

  save() {
    try {
      sessionStorage.setItem(this.key, JSON.stringify({ page: this.page, y: Math.round(window.scrollY) }));
    } catch (error) {
      // private mode or storage off: nothing to restore later, the list still works
    }
  }

  async restore(force) {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.key));
    } catch (error) {
      return;
    }
    if (!saved || saved.page <= this.page) return;
    const nav = performance.getEntriesByType('navigation')[0];
    const cameBack = nav && (nav.type === 'back_forward' || nav.type === 'reload');
    if (!cameBack && !force) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    while (this.page < saved.page && this.page < this.pages) {
      const added = await this.load('restore');
      if (!added) break;
    }
    window.scrollTo(0, saved.y);
    this.autoLoads = this.autoLimit; // the shopper had already scrolled this far; from here the button takes over
  }
}

if (!customElements.get('pp-more-products')) customElements.define('pp-more-products', PPMoreProducts);
